import React, { useEffect, useState } from "react";
import { Copy, Trash2, Reply, CheckCircle2, RotateCcw, X } from "lucide-react";

// Acrobat-like popup for the selected comment: author, date, editable text, quick actions.
export function CommentPopup({ c, left, top, cm, t, dateFmt, autoFocus, onEdited, onCopy, onReply, onDelete, onClose }) {
    const [text, setText] = useState(c.text || "");
    const editable = cm.canEdit(c);
    useEffect(() => { setText(c.text || ""); }, [c.id, c.text]);
    const save = () => { if (text !== (c.text || "")) cm.update(c.id, { text }); onEdited(); };
    const btn = "h-7 w-7 flex items-center justify-center rounded text-slate-400 hover:text-white hover:bg-[#1e293b] transition-colors";

    return (
        <div data-testid="comment-popup" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
            className="absolute z-[65] w-[260px] rounded-lg border border-[#1e293b] bg-[#111827]/95 backdrop-blur-md shadow-2xl text-slate-100"
            style={{ left, top }}>
            <div className="flex items-start justify-between gap-2 px-3 pt-2.5">
                <div className="min-w-0">
                    <div data-testid="comment-popup-author" className="text-sm font-semibold truncate">{c.author?.name}</div>
                    <div data-testid="comment-popup-date" className="text-[11px] font-mono text-slate-400">{dateFmt(c.created_at)} · {t(`cm_${c.type}`)}</div>
                </div>
                <button data-testid="comment-popup-close" onClick={onClose} className={btn}><X className="w-3.5 h-3.5" /></button>
            </div>
            {c.resolved && <div className="mx-3 mt-2 text-[11px] font-mono text-emerald-400">✓ {t("cm_resolved_by", { n: c.resolved_by?.name || "" })}</div>}
            <div className="px-3 py-2">
                {editable ? (
                    <textarea data-testid="comment-popup-text" autoFocus={autoFocus} value={text} rows={3}
                        onChange={(e) => setText(e.target.value)} onBlur={save}
                        onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) e.currentTarget.blur(); }}
                        placeholder={t("cm_text_ph")}
                        className="w-full resize-y bg-[#0b0f17] border border-[#1e293b] rounded-md text-sm px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500/60" />
                ) : (
                    <p data-testid="comment-popup-text-ro" className="text-sm whitespace-pre-wrap text-slate-200">{c.text || "—"}</p>
                )}
                {(c.replies || []).length > 0 && (
                    <div className="text-[11px] text-slate-400 mt-1">{t("cm_replies_n", { n: c.replies.length })}</div>
                )}
            </div>
            <div className="flex items-center gap-0.5 border-t border-[#1e293b] px-2 py-1">
                <button data-testid="comment-popup-reply" onClick={onReply} className={btn} title={t("cm_reply")}><Reply className="w-3.5 h-3.5" /></button>
                <button data-testid="comment-popup-resolve" onClick={() => cm.update(c.id, { resolved: !c.resolved })} className={btn}
                    title={c.resolved ? t("cm_reopen") : t("cm_resolve")}>
                    {c.resolved ? <RotateCcw className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                </button>
                <button data-testid="comment-popup-copy" onClick={onCopy} className={btn} title={t("cm_copy")}><Copy className="w-3.5 h-3.5" /></button>
                {editable && (
                    <button data-testid="comment-popup-delete" onClick={onDelete} className={`${btn} hover:text-rose-400 ml-auto`} title={t("cm_delete")}>
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}
